/**
 * LandingHero Web Component
 * Manages video modal popup, focus trapping, iframe/video playback lifecycle,
 * smooth scrolling to purchase section, and Shopify Theme Editor events.
 */

// Global scroll to purchase helper
function scrollToPurchaseSection(e) {
  if (e) e.preventDefault();
  const target =
    document.getElementById('landing-kit-select') ||
    document.getElementById('purchase-section') ||
    document.querySelector('.landing-product-purchase') ||
    document.querySelector('[data-section-id="landing-product-purchase"]');

  if (target) {
    const header =
      document.querySelector('.landing-header') ||
      document.querySelector('.header-wrapper') ||
      document.querySelector('sticky-header');
    const headerOffset = header ? header.offsetHeight + 10 : 80;
    const targetPosition = target.getBoundingClientRect().top + window.pageYOffset - headerOffset;

    window.scrollTo({
      top: Math.max(0, targetPosition),
      behavior: 'smooth'
    });
  }
}

// Global click delegation for purchase scroll buttons
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-action="scroll-to-purchase"], .landing-hero__btn-primary, .landing-problem__btn, a[href*="#purchase-section"], a[href*="#landing-kit-select"]');
  if (btn) {
    const href = btn.getAttribute('href') || '';
    if (href.startsWith('#') || href === '' || href.includes('#purchase-section') || href.includes('#landing-kit-select')) {
      scrollToPurchaseSection(e);
    }
  }
});

class LandingHero extends HTMLElement {
  constructor() {
    super();

    this.modal = this.querySelector('.landing-hero-modal');
    this.openBtn = this.querySelector('[data-action="open-video-modal"]');
    this.closeBtns = this.querySelectorAll('[data-action="close-video-modal"]');
    this.videoContainer = this.querySelector('.landing-hero-modal__media');
    this.html5Video = this.querySelector('.landing-hero-modal__video');
    this.iframeVideo = this.querySelector('.landing-hero-modal__iframe');

    this.onKeydownBound = this.onKeydown.bind(this);
  }

  connectedCallback() {
    this.primaryBtn = this.querySelector('.landing-hero__btn-primary');
    if (this.primaryBtn) {
      this.primaryBtn.addEventListener('click', (e) => {
        const href = this.primaryBtn.getAttribute('href') || '';
        if (href.startsWith('#') || href === '' || href === '#purchase-section' || href === '#landing-kit-select') {
          scrollToPurchaseSection(e);
        }
      });
    }

    if (this.openBtn) {
      this.openBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.openModal();
      });
    }

    this.closeBtns.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.closeModal();
      });
    });

    document.addEventListener('keydown', this.onKeydownBound);

    // Shopify Theme Editor Lifecycle
    this.ownerDocument.addEventListener('shopify:section:load', this.onSectionLoad.bind(this));
    this.ownerDocument.addEventListener('shopify:section:unload', this.onSectionUnload.bind(this));
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydownBound);
  }

  openModal() {
    if (!this.modal) return;

    this.modal.classList.add('is-open');
    this.modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    // Play HTML5 video if present
    if (this.html5Video) {
      this.html5Video.currentTime = 0;
      const playPromise = this.html5Video.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          // Autoplay was prevented
        });
      }
    }

    // If iframe video (YouTube / Vimeo), set autoplay in src
    if (this.iframeVideo && this.iframeVideo.dataset.src) {
      const baseSrc = this.iframeVideo.dataset.src;
      const separator = baseSrc.includes('?') ? '&' : '?';
      this.iframeVideo.src = `${baseSrc}${separator}autoplay=1`;
    }

    // Focus close button for accessibility
    const closeBtn = this.modal.querySelector('.landing-hero-modal__close');
    if (closeBtn) {
      setTimeout(() => closeBtn.focus(), 50);
    }
  }

  closeModal() {
    if (!this.modal) return;

    this.modal.classList.remove('is-open');
    this.modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    // Pause HTML5 video
    if (this.html5Video) {
      this.html5Video.pause();
    }

    // Reset iframe to stop playback
    if (this.iframeVideo) {
      this.iframeVideo.src = '';
    }

    // Return focus to trigger button
    if (this.openBtn) {
      this.openBtn.focus();
    }
  }

  onKeydown(e) {
    if (e.key === 'Escape' && this.modal && this.modal.classList.contains('is-open')) {
      this.closeModal();
    }
  }

  onSectionLoad(e) {
    if (e.target === this.closest('.shopify-section')) {
      // Re-init if reloaded in theme editor
    }
  }

  onSectionUnload(e) {
    if (e.target === this.closest('.shopify-section')) {
      document.body.style.overflow = '';
    }
  }
}

customElements.define('landing-hero', LandingHero);
